import { Component } from '@theme/component';

/**
 * A small utility for setting up an animated disclosure panel using inert and CSS display grid for height auto animation.
 *
 * This component works with any element that follows the details/summary pattern for revealing content.
 * Forms and other interactive elements don't need to live inside a details element, but can still use
 * the expanded pattern for accessibility and animation.
 *
 * The component relies on CSS display grid and inert attributes to create smooth height animations
 * when expanding/collapsing content areas.
 *
 * @class DisclosureCustom
 * @typedef {object} Refs
 * @property {HTMLElement} disclosureTrigger – The disclosure trigger element.
 * @property {HTMLElement} disclosureContent – The disclosure content element.
 *
 * @extends Component<Refs>
 */
class DisclosureCustom extends Component {
  requiredRefs = ['disclosureTrigger', 'disclosureContent'];

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  /**
   * Toggles the disclosure panel open/closed state.
   * Updates aria-expanded, aria-label attributes on the trigger and inert state on content.
   * Works with CSS grid animations to provide smooth height transitions.
   */
  toggleDisclosure = () => {
    // Fix : on re-query les éléments frais depuis le DOM (et non this.refs en cache).
    // Après un morph du panier (ex. ajout au panier / manchon offert), le composant
    // reste en place mais ses enfants sont remplacés → this.refs pointe vers d'anciens
    // nœuds détachés et le 1er clic n'ouvrait rien. Le fallback garde le comportement natif.
    const trigger = this.querySelector('[ref="disclosureTrigger"]') || this.refs.disclosureTrigger;
    const content = this.querySelector('[ref="disclosureContent"]') || this.refs.disclosureContent;
    if (!trigger || !content) return;
    const expanded = trigger.matches('[aria-expanded="true"]');
    trigger.setAttribute('aria-expanded', String(!expanded));
    trigger.setAttribute(
      'aria-label',
      `${expanded ? trigger.dataset.disclosureOpen : trigger.dataset.disclosureClose}`
    );
    content.inert = expanded;

    // iOS Safari ne recalcule pas toujours grid-template-rows (0fr → 1fr) quand on
    // retire `inert` dynamiquement → le contenu reste à hauteur 0 et le panneau
    // semble ne pas s'ouvrir (alors que l'état JS est bien à jour). On force un
    // reflow complet à l'ouverture pour garantir le recalcul de la grille.
    if (!expanded) {
      const prevDisplay = content.style.display;
      content.style.display = 'none';
      void content.offsetHeight; // force reflow
      content.style.display = prevDisplay;
    }
  };
}

if (!customElements.get('disclosure-custom')) {
  customElements.define('disclosure-custom', DisclosureCustom);
}
